import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react-dom';

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

interface PortalTooltipProps {
  referenceEl: HTMLElement | null;
  show: boolean;
  isTutorial?: boolean;
  children: React.ReactNode;
}

const PortalTooltip: React.FC<PortalTooltipProps> = ({
  referenceEl,
  show,
  isTutorial = false,
  children,
}) => {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  useBrowserLayoutEffect(() => {
    setPortalRoot(document.body);
  }, []);

  const { refs, floatingStyles, middlewareData, isPositioned } = useFloating({
    open: show && referenceEl !== null,
    elements: { reference: referenceEl },
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });

  const shiftX = typeof middlewareData.shift?.x === 'number' ? middlewareData.shift.x : 0;
  const tooltipStyles: React.CSSProperties & { '--tooltip-shift-x': string } = {
    ...floatingStyles,
    visibility: isPositioned ? 'visible' : 'hidden',
    '--tooltip-shift-x': `${shiftX}px`,
  };

  if (!show || !portalRoot || !referenceEl) return null;

  return createPortal(
    <div
      ref={refs.setFloating}
      role="tooltip"
      style={tooltipStyles}
      className={`tooltip-arrow pointer-events-none visible z-tutorial-active m-0 max-h-none w-max max-w-tooltip min-w-tooltip cursor-default overflow-visible rounded-md border border-outline-alt bg-raised px-3 py-2 text-center ${isTutorial ? 'text-body leading-normal' : 'text-small leading-snug'} font-normal break-words text-glyph-secondary opacity-100 shadow-tooltip select-none`}
    >
      {children}
    </div>,
    portalRoot
  );
};

export default PortalTooltip;
